import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useFormWithValidation } from '@/shared/lib/hooks/use-form-with-validation';
import { useChangePasswordMutation } from '@/entities/user';

import { createPasswordSchema, type PasswordFormValues } from './validation';

interface UseChangePasswordFormProps {
  onSuccess?: () => void;
  open: boolean;
}

export const useChangePasswordForm = ({ onSuccess, open }: UseChangePasswordFormProps) => {
  const { t } = useTranslation();
  const [changePasswordMutation, { isLoading }] = useChangePasswordMutation();
  const [alertState, setAlertState] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const passwordSchema = createPasswordSchema(t);
  const form = useFormWithValidation(passwordSchema);

  const onSubmit = useCallback(
    async (data: PasswordFormValues) => {
      setAlertState(null);
      try {
        await changePasswordMutation({
          oldPassword: data.oldPassword,
          newPassword: data.newPassword
        }).unwrap();

        setAlertState({
          type: 'success',
          message: t('password.changeSuccess', 'Password changed successfully')
        });

        form.reset();
        onSuccess?.();
      } catch (_error) {
        setAlertState({
          type: 'error',
          message: t('password.changeError', 'Failed to change password. Make sure the old password is correct.')
        });
      }
    },
    [changePasswordMutation, form, t, onSuccess]
  );

  useEffect(() => {
    if (!open) {
      setAlertState(null);
      form.reset();
    }
  }, [form, open]);

  return {
    form,
    onSubmit,
    alertState,
    isLoading
  };
};
